import React, { useState, useEffect, useRef } from "react";
import { auth } from "../firebase-config";
import BotImage from "./BotImage.svg";
import UserImage from "./UserImage.svg";
import styles from "./LandingPage.module.css";
import {
    Box,
    TextField,
    Typography,
    Avatar,
    IconButton,
    Paper,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
// REMOVE AXIOS IMPORT - WE WILL USE FIRESTORE SDK NOW
// import axios from "axios"; 
import TypingIndicator from "./TypingIndicator";
import { useSearchParams } from "react-router-dom";
// IMPORT FIRESTORE FUNCTIONS
import {
    collection,
    doc,
    setDoc,
    addDoc,
    serverTimestamp,
    getDoc,
    arrayUnion,
    query,
    orderBy,
    increment,
    updateDoc,
    onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase-config";
import UserAvatar from "./UserAvatar";
import { alpha, useTheme } from '@mui/material/styles';
import { onAuthStateChanged } from 'firebase/auth';
import { CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { createThread, addMessageToThread, getThreads, deleteThread, renameThread } from "../services/ThreadsService";
import ThreadsSidebar, { EXPANDED_WIDTH, COLLAPSED_WIDTH } from './ThreadsSidebar';
import TherapyReferralBanner from './TherapyReferralBanner';

export const ChatPage = ({ user, isDarkMode, sidebarOpen, setSidebarOpen }) => {
    console.log('User in ChatPage:', user);
    const searchParams = useSearchParams();
    const [btnDisabled, setBtnDisabled] = useState(true);
    const [firstMsgFromUser] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const emotion = params.get("emotion");
        const firstMessage = params.get("first");
        const isNewClick = localStorage.getItem('new_emotion_click') === 'true';
        
        // Check for any pending conversation
        const pendingConversation = localStorage.getItem('pendingConversation');
        if (pendingConversation) {
            return [];
        }
        
        if (emotion && isNewClick) {
            // Clear the flag immediately
            localStorage.removeItem('new_emotion_click');
            return [{
                content: `${emotion}`,
                role: "user",
            }];
        } else if (firstMessage) {
            // Handle message from text input on landing page
            return [{
                content: firstMessage,
                role: "user",
            }];
        }
        return [];
    });
    const [messages, setMessages] = useState(firstMsgFromUser);
    const [input, setInput] = useState("");
    const [userLocation, setUserLocation] = useState({ latitude: null, longitude: null });
    const [text, setDefaultState] = useState("");
    const [botTyping, setBotTyping] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const [currentThreadId, setCurrentThreadId] = useState(null);
    const [threads, setThreads] = useState([]);
    const [showReferralCard, setShowReferralCard] = useState(false);
    const theme = useTheme();
    const threadCreationPromiseRef = useRef(null);
    // Add a ref to track which messages are being processed to prevent duplicates
    const messageProcessingRef = useRef(new Set());

    const getUserLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                setUserLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            });
        } else {
            alert("Geolocation is not supported by this browser.");
        }
    };

    const messageEndRef = useRef(null);

    const handleInputChange = (event) => {
        setBtnDisabled(!event.target.value);
        setInput(event.target.value);
        setDefaultState(event.target.value);
    };

    const handleMessageSubmit = async (e) => {
        e.preventDefault();
        if (input.trim() === "") return;

        try {
            // Remove subscription status and message limit check
            const userRef = doc(db, "Users", auth.currentUser.uid);
            // Keep fetching userData if needed for interactionCount, otherwise remove parts of this
            const userDoc = await getDoc(userRef);
            const userData = userDoc.data(); // May not be needed anymore

            // Removed check: if (userData.subscriptionStatus === "free" && userData.freeMessagesRemaining <= 0) { ... }

            // Generate a unique ID for this message
            const messageId = Date.now().toString();
            
            // Add user message to UI first with processed flag and ID
            const userMessage = { 
                role: "user", 
                content: input, 
                id: messageId,
                processed: true // Mark as processed immediately to prevent handleFirstMsg from picking it up
            };
            
            // Track that we're processing this message
            messageProcessingRef.current.add(messageId);
            
            setMessages((prevMessages) => [...prevMessages, userMessage]);
            
            // Clear input field and set typing indicator
            setInput("");
            setDefaultState("");
            setBtnDisabled(true);
            setBotTyping(true);

            // Store user message to Firestore and get thread ID
            console.log("Sending user message to Firestore...");
            const threadId = await sendMessageToFirestore(input, "user");
            console.log("Message sent, thread ID:", threadId);
            
            // Keep interactionCount update
            if (auth.currentUser) { // Ensure user is still logged in
                await updateDoc(userRef, {
                    interactionCount: increment(1)
                });
                console.log("Updated interaction count");
            } else {
                 console.log("User not logged in, skipping interaction count update");
            }

            // Get bot response from backend
            console.log("Getting bot response...");
            const response = await sendMessageToBackend(input);
            setBotTyping(false);
            
            if (response && response.message) {
                const { message: botText, flag } = response;

                // Handle referral card
                if (flag === "chronic_issue") {
                    setShowReferralCard(true);
                }

                // Add bot message to UI
                const botMessage = { role: "system", content: botText };
                setMessages((prevMessages) => [...prevMessages, botMessage]);
                
                // Store bot message to Firestore - use the thread ID we stored the user message in
                if (threadId) {
                    console.log("Storing bot response to thread:", threadId);
                    await addMessageToThread(auth.currentUser.uid, threadId, {
                        content: botText,
                        role: "system"
                    });
                } else {
                    console.error("No thread ID available for storing bot response");
                }
            } else {
                console.error("No response received from bot");
            }
        } catch (error) {
            console.error("Error handling message:", error);
            setBotTyping(false);
            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    role: "system",
                    content: "Sorry, there was an error. Please try again.",
                },
            ]);
        }
    };

    // MODIFY sendMessageToFirestore FUNCTION
    const sendMessageToFirestore = async (message, role) => {
        if (!auth.currentUser) {
            console.error("No authenticated user found");
            return null;
        }

        const userId = auth.currentUser.uid;
        const messageObj = {
            content: message,
            role: role
        };
        
        try {
            if (currentThreadId) {
                console.log("Adding message to existing thread:", currentThreadId);
                await addMessageToThread(userId, currentThreadId, messageObj);
                return currentThreadId;
            }
            
            if (threadCreationPromiseRef.current) {
                console.log("Awaiting ongoing thread creation promise");
                const existingThreadId = await threadCreationPromiseRef.current;
                await addMessageToThread(userId, existingThreadId, messageObj);
                return existingThreadId;
            } else {
                threadCreationPromiseRef.current = (async () => {
                    try {
                        console.log("Creating new thread using promise");
                        const newThreadId = await createThread(userId, messageObj);
                        console.log("Thread created successfully:", newThreadId);
                        setCurrentThreadId(newThreadId);
                        return newThreadId;
                    } catch (error) {
                        console.error("Error creating thread:", error);
                        throw error;
                    } finally {
                        threadCreationPromiseRef.current = null;
                    }
                })();
                const newThreadId = await threadCreationPromiseRef.current;
                return newThreadId;
            }
        } catch (error) {
            console.error("Error in sendMessageToFirestore:", error);
            throw error;
        }
    };

    const sendMessageToBackend = async (message) => {
        if (!auth.currentUser) {
            console.error("No authenticated user found");
            throw new Error("No authenticated user found");
        }

        const userId = auth.currentUser.uid;
        const emotionData = localStorage.getItem('emotionButtonData');
        const isEmotionButtonMessage = emotionData ? JSON.parse(emotionData).isEmotionButton : false;

        try {
            const response = await fetch('/api/chatbot', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
                },
                body: JSON.stringify({
                    message,
                    latitude: userLocation.latitude,
                    longitude: userLocation.longitude,
                    conversationHistory: messages,
                    isEmotionButtonMessage,
                }),
            });

            // Clear the emotion data after sending
            localStorage.removeItem('emotionButtonData');

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Backend error:", errorText);
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();

            return { message: data.message, flag: data.flag };
        } catch (error) {
            console.error("Error in sendMessageToBackend:", error);
            throw error;
        }
    };

    const handleFirstMsg = async () => {
        if (messages.length === 0) return;
        
        const firstMsg = messages[0];
        
        // If message is already processed or being processed, skip
        if (firstMsg.processed || messageProcessingRef.current.has(firstMsg.id)) {
            return;
        }
        
        // Mark message as being processed
        const messageId = firstMsg.id || Date.now().toString();
        messageProcessingRef.current.add(messageId);
        
        setBotTyping(true);
        
        try {
            // Update the message with processed flag and ID
            const processedMsg = { ...firstMsg, processed: true, id: messageId };
            setMessages([processedMsg]);
            
            const userId = auth.currentUser.uid;
            
            const messageObj = {
                content: processedMsg.content,
                role: "user"
            };
            
            let threadId = currentThreadId;
            if (!threadId) {
                if (threadCreationPromiseRef.current) {
                    console.log("Awaiting existing thread creation promise for first message");
                    threadId = await threadCreationPromiseRef.current;
                } else {
                    threadCreationPromiseRef.current = (async () => {
                        try {
                            console.log("Creating new thread for first message using promise");
                            const newThreadId = await createThread(userId, messageObj);
                            console.log("New thread created with ID:", newThreadId);
                            setCurrentThreadId(newThreadId);
                            return newThreadId;
                        } catch (error) {
                            console.error("Error creating thread for first message:", error);
                            throw error;
                        } finally {
                            threadCreationPromiseRef.current = null;
                        }
                    })();
                    threadId = await threadCreationPromiseRef.current;
                }
            }
            
            // Get bot response
            const response = await sendMessageToBackend(processedMsg.content);
            setBotTyping(false);
            
            // Add bot message to UI
            if (response && response.message) {
                const { message: botText, flag } = response;

                // Handle referral card
                if (flag === "chronic_issue") {
                    setShowReferralCard(true);
                }

                const botMessage = { role: "system", content: botText };
                setMessages((prevMessages) => [...prevMessages, botMessage]);
    
                // Add bot message to thread
                await addMessageToThread(userId, threadId, {
                    content: botText,
                    role: "system"
                });
            } else {
                console.error("No response received from bot");
            }
        } catch (error) {
            console.error("Error processing first message:", error);
            setBotTyping(false);
            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    role: "system",
                    content: "Sorry, there was an error. Please try again.",
                },
            ]);
        } finally {
            // Remove from processing set when done
            messageProcessingRef.current.delete(messageId);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            handleMessageSubmit(event);
        }
    };

    useEffect(() => {
        const userId = auth.currentUser?.uid;
        if (userId) {
            fetchMessages();
        }
    }, [auth.currentUser]);
    
        const fetchMessages = async () => {
            try {
                if (!currentThreadId) {
                    // If no thread is selected, get the most recent thread
                    const threads = await getThreads(auth.currentUser.uid);
                    if (threads.length > 0) {
                        setCurrentThreadId(threads[0].id);
                        setMessages(threads[0].messages.map(msg => ({
                            role: msg.from === "user" ? "user" : "system",
                            content: msg.text,
                            timestamp: msg.timestamp,
                            processed: true
                        })));
                    }
                }
            } catch (error) {
                console.error("Error fetching messages:", error);
                setMessages(firstMsgFromUser);
            }
        };

    useEffect(() => {
        console.log("Current messages:", messages);
        
        // Find the first unprocessed message that isn't already being processed
        const unprocessedMsg = messages.find(msg => 
            msg.role === "user" && 
            !msg.processed && 
            !messageProcessingRef.current.has(msg.id || "")
        );
        
        // Only process if:
        // 1. There's an unprocessed message
        // 2. It's from a special source (URL params or emotion button)
        // 3. It's not already being processed
        if (unprocessedMsg && 
            (window.location.search.includes("emotion=") || 
             window.location.search.includes("first=") || 
             localStorage.getItem('emotionButtonData'))) {
            
            console.log("Calling handleFirstMsg for initial message");
            handleFirstMsg();
        }
        
        getUserLocation();
    }, [messages]); 

    useEffect(() => {
        if (messageEndRef.current) {
            messageEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    useEffect(() => {
        const isEmotionButtonMessage = localStorage.getItem('isEmotionButtonMessage') === 'true';
        if (isEmotionButtonMessage) {
            console.log("Emotion button message detected on ChatPage mount");
            // You can set a state here if needed
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setIsLoading(false);
            } else {
                // Keep redirect logic
                setTimeout(() => {
                    if (!auth.currentUser) {
                        // Redirect if not logged in after a delay
                        // Consider removing redirectedFromPayment state if no longer relevant
                        navigate('/'); 
                    }
                }, 1500); // Slightly shorter delay might be okay now
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const success = params.get('success') === 'true';

        if (success) {
            const emotionData = localStorage.getItem('emotionButtonData');
            const savedConversation = localStorage.getItem('pendingConversation');
            
            if (emotionData) {
                const { message } = JSON.parse(emotionData);
                setInput(message);
                // Will trigger message send through the input effect
            } else if (savedConversation) {
                setMessages(JSON.parse(savedConversation));
            }
            
            localStorage.removeItem('pendingConversation');
            localStorage.removeItem('emotionButtonData');
            
            const newUrl = `${window.location.pathname}${window.location.hash}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, [location]);

    // Add this effect to store conversation updates
    useEffect(() => {
        if (messages.length > 0) {
            localStorage.setItem('currentConversation', JSON.stringify(messages));
        }
    }, [messages]);

    useEffect(() => {
        if (user?.uid) {
            // Set up real-time listener for threads
            const threadsRef = collection(db, "Users", user.uid, "threads");
            const q = query(threadsRef, orderBy("updated_at", "desc"));
            
            const unsubscribe = onSnapshot(q, (snapshot) => {
                const updatedThreads = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setThreads(updatedThreads);
            });

            // Cleanup listener on unmount
            return () => unsubscribe();
        }
    }, [user?.uid]);

    const handleThreadSelect = async (threadId) => {
        try {
            setCurrentThreadId(threadId);
            const userRef = doc(db, "Users", auth.currentUser.uid);
            const threadRef = doc(userRef, "threads", threadId);
            const threadDoc = await getDoc(threadRef);
            
            if (threadDoc.exists()) {
                const threadData = threadDoc.data();
                if (threadData.messages) {
                    setMessages(threadData.messages.map(msg => ({
                        role: msg.from,
                        content: msg.text,
                        timestamp: msg.timestamp,
                        processed: true
                    })));
                }
            }
        } catch (error) {
            console.error("Error loading thread messages:", error);
        }
    };

    const handleThreadDelete = async (threadId) => {
        try {
            await deleteThread(auth.currentUser.uid, threadId);
            setThreads(threads.filter(t => t.id !== threadId));
            if (currentThreadId === threadId) {
                setCurrentThreadId(null);
                setMessages([]);
            }
        } catch (error) {
            console.error("Error deleting thread:", error);
        }
    };

    const handleThreadRename = async (threadId) => {
        try {
            const thread = threads.find(t => t.id === threadId);
            if (!thread) return;
            
            // Prompt the user for a new title
            const newTitle = window.prompt("Enter a new title for this conversation:", thread.title);
            
            if (newTitle && newTitle.trim() !== "" && newTitle !== thread.title) {
                // Update in Firestore
                await renameThread(auth.currentUser.uid, threadId, newTitle);
                
                // Update locally
                setThreads(threads.map(t => 
                    t.id === threadId 
                        ? {...t, title: newTitle} 
                        : t
                ));
            }
        } catch (error) {
            console.error("Error renaming thread:", error);
        }
    };

    const handleSidebarToggle = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const handleNewThread = async () => {
        setMessages([]);
        setCurrentThreadId(null);
        threadCreationPromiseRef.current = null;
        console.log("Starting new thread - cleared messages, thread ID, and promise");
    };

    useEffect(() => {
        console.log("Resetting thread creation promise on mount");
        threadCreationPromiseRef.current = null;
        messageProcessingRef.current.clear();
        
        return () => {
            console.log("Resetting thread creation promise on unmount");
            threadCreationPromiseRef.current = null;
            messageProcessingRef.current.clear();
        };
    }, []);

    if (isLoading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                bgcolor: 'background.default'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                height: '100vh',
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: theme.palette.background.default,
            }}
        >
            <ThreadsSidebar
                threads={threads}
                currentThreadId={currentThreadId}
                onThreadSelect={handleThreadSelect}
                onThreadDelete={handleThreadDelete}
                onThreadRename={handleThreadRename}
                isDarkMode={isDarkMode}
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                onNewThread={handleNewThread}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    zIndex: 1,
                    ml: {
                        xs: `${COLLAPSED_WIDTH}px`,
                        sm: `${COLLAPSED_WIDTH}px`
                    },
                    pr: {
                        xs: 0,
                        sm: 0
                    },
                    transition: theme.transitions.create(['margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                        width: `calc(100% - ${COLLAPSED_WIDTH}px)`,
                    }
                }}
            >
                <div style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                }}>
                    <Box 
                        className={`${styles.chatpage} chat-page-container`} 
                        sx={{
                            width: '100%',
                            maxWidth: {
                                xs: '100%',
                                sm: '800px'
                            },
                            margin: '0 auto',
                            paddingLeft: {
                                xs: 0,
                                sm: sidebarOpen ? `${EXPANDED_WIDTH - COLLAPSED_WIDTH}px` : 0
                            },
                            transition: theme.transitions.create(['padding'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        }}
                    >
                        <Box sx={{ 
                            flexGrow: 1, 
                            overflow: "auto", 
                            p: {
                                xs: 2,
                                sm: 5
                            },
                            mb: {
                                xs: 1,
                                sm: 2
                            },
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '3px',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                }
                            },
                            [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                                padding: '8px 12px',
                            }
                        }}>
                            {messages.map((message, index) => (
                                <Message key={index} message={message} isDarkMode={isDarkMode} />
                            ))}
                            {showReferralCard && (
                                <TherapyReferralBanner 
                                    variant="prominent" 
                                    isDarkMode={isDarkMode} 
                                />
                            )}
                            {botTyping && <TypingIndicator />}
                            <div ref={messageEndRef} />
                        </Box>

                        <div className={styles.chatbottom} 
                             style={{
                                 position: 'sticky',
                                 bottom: 0,
                                 width: '100%',
                                 zIndex: 1, // Ensure it stacks above the sidebar
                                 backgroundColor: theme.palette.background.default
                             }}>
                            <TherapyReferralBanner variant="subtle" isDarkMode={isDarkMode} />
                            <Box className={styles.messagetextareaframe} sx={{
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(245, 209, 199, 0.35)',
                                mx: {
                                    xs: 2,
                                    sm: 2
                                },
                                mb: {
                                    xs: 0.5,
                                    sm: 2
                                },
                                borderRadius: {
                                    xs: '20px',
                                    sm: '30px'
                                },
                                p: {
                                    xs: 1,
                                    sm: 0.5
                                },
                                // Mobile-specific padding adjustments
                                [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                                    width: 'calc(100% - 16px)', // 8px on each side
                                    boxSizing: 'border-box',
                                    marginLeft: '8px', // Add left margin to prevent touching sidebar
                                    marginRight: '8px', // Add right margin to prevent touching screen edge
                                }
                            }}>
                                <TextField
                                    className={styles.typeYourMessage}
                                    id="text-field"
                                    placeholder="Type your message..."
                                    color="primary"
                                    variant="standard"
                                    multiline
                                    fullWidth
                                    InputProps={{ 
                                        disableUnderline: true,
                                        sx: {
                                            px: {
                                                xs: 1.5,
                                                sm: 1.5
                                            },
                                            py: {
                                                xs: 0.75,
                                                sm: 0.5
                                            },
                                            // Ensure proper text field width on mobile
                                            [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                                                width: '100%',
                                                boxSizing: 'border-box'
                                            }
                                        }
                                    }}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    value={text}
                                />
                                <IconButton
                                    color="primary"
                                    disabled={btnDisabled}
                                    onClick={handleMessageSubmit}
                                    style={{ color: btnDisabled ? "#3b3b3b" : "inherit" }}
                                    sx={{
                                        p: {
                                            xs: 0.75,
                                            sm: 0.75
                                        }
                                    }}
                                >
                                    <SendIcon />
                                </IconButton>
                            </Box>
                        </div>
                    </Box>
                </div>
            </Box>
        </Box>
    );
};

const Message = ({ message, isDarkMode }) => {
  console.log("Rendering message:", message.content);
  const theme = useTheme();
  const isBot = message.role === "system";

  const renderContent = (content) => {
    if (!content) return null;
    const parts = content.split(/(\[.*?\]\(.*?\))/g);
    return parts.map((part, index) => {
      const match = part.match(/\[(.*?)\]\((.*?)\)/);
      if (match) {
        return (
          <a key={index} href={match[2]} target="_blank" rel="noopener noreferrer">
            {match[1]}
          </a>
        );
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isBot ? "flex-start" : "flex-end",
        alignItems: 'flex-start',
        mb: 2,
        mx: {
          xs: -1,
          sm: 0
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isBot ? "row" : "row-reverse",
          alignItems: 'flex-start',
          width: '100%',
          [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
            flexWrap: 'nowrap', // Prevent wrapping
            maxWidth: '100%'
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: {
              xs: '40px',
              sm: '50px'
            },
            mx: 0.5,
          }}
        >
          {isBot ? (
            <Avatar
              sx={{
                bgcolor: "primary.main",
                width: {
                  xs: 32,
                  sm: 40
                },
                height: {
                  xs: 32,
                  sm: 40
                }
              }}
              alt="BotImage"
              src={BotImage}
            />
          ) : (
            <UserAvatar 
              sx={{
                width: {
                  xs: 32,
                  sm: 40
                },
                height: {
                  xs: 32,
                  sm: 40
                }
              }}
            />
          )}
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: {
                xs: '0.75rem',
                sm: '0.875rem'
              }
            }}
          >
            {isBot ? "Buddy" : "You"}
          </Typography>
        </Box>
        <Paper
          variant="outlined"
          sx={{
            p: {
              xs: 1.5,
              sm: 2
            },
            ml: isBot ? 0.5 : 0,
            mr: isBot ? 0 : 0.5,
            backgroundColor: isBot
              ? (isDarkMode ? "primary.dark" : alpha(theme.palette.primary.light, 0.5))
              : (isDarkMode ? "secondary.dark" : "secondary.light"),
            borderRadius: isBot
              ? "20px 20px 20px 5px"
              : "20px 20px 5px 20px",
            maxWidth: {
              xs: '80%',
              sm: '80%'
            },
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            width: 'auto',
            display: 'inline-block',
            [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
              mr: isBot ? '8px' : 0,
            }
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              overflowWrap: "break-word", 
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              hyphens: "auto",
              fontSize: {
                xs: '0.9rem',
                sm: '1rem'
              }
            }}
          >
            {renderContent(message.content)}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default ChatPage;